import { type RouterOutputs } from './trpc';

// Bot group chat room names used for the acknowledge feature
export const BOT_ROOM_NAMES = [
  'Admin Room',
  'Account Deletion Requests',
  'Contact Room',
  'Spam Room'
] as const;

// OPEN means the message window id open
// CLOSED means the message window is closed, but is in the conversationList
// MINIMIZED means the message window is minimized
// FILTERED means the conversation is hidden from the conversationList
export type ConversationState = 'OPEN' | 'CLOSED' | 'MINIMIZED' | 'FILTERED';
export type Conversation = ConversationDto & {
  usersTyping: number[];
};

export type GetConversationsResponse = RouterOutputs['messages']['get'];
export type GetMessagesResponse = RouterOutputs['messages']['getMessages'];
export type ConversationDto =
  RouterOutputs['messages']['get']['conversations'][0];
export type ConversationParticipant = ConversationDto['participants'][0];

export type ChatSearchResponse = RouterOutputs['messages']['search'];
export type ChatSearchConversation = ChatSearchResponse['conversations'][0];
export type ChatSearchMessage = ChatSearchResponse['messages'][0];

/**
 * A message to open a conversation AT — a link, a push or a chat search hit.
 * Only the id travels: `messages.getMessages { aroundId }` resolves it inside
 * the conversation, to the root row to scroll to and, for a thread reply, the
 * reply to scroll to inside its thread.
 */
export type MessageFocus = { messageId: number };

/**
 * What the reader is told when a link, a push or a bell row points at a chat
 * they cannot open. Deliberately the same whether the chat is private, gone,
 * or one they were removed from: which of those it is is not their business.
 */
export const CHAT_UNAVAILABLE_MESSAGE =
  "This chat isn't available to your account. It may be private, or the link may be wrong.";

/**
 * How a channel names itself in a chat list or window header.
 *
 * A factiii channel IS the factiii (each factiii has exactly one chat), so it
 * reads as `f/<slug>` — the same address the factiii carries everywhere else.
 * Plain "General" is indistinguishable from a group chat named General, which
 * is what the space-and-factiii avatar pair beside it also fixes. A space
 * channel keeps the `#` convention.
 *
 * Returns null for DMs, group chats and ban disputes — those name themselves
 * after their participants.
 */
export const channelLabel = (conversation: {
  kind?: string;
  name: string | null;
  factiii?: { slug: string } | null;
}): string | null => {
  if (conversation.kind !== 'CHANNEL') return null;
  if (conversation.factiii) return `f/${conversation.factiii.slug}`;
  return `#${conversation.name ?? 'chat'}`;
};
// Space-scoped channel as listed by messages.channels (browse view — the
// joined conversation itself flows through ConversationDto like any other).
export type ChannelDto = RouterOutputs['messages']['channels']['channels'][0];

export type MessageAttachment = {
  id: string;
  key: string;
  type: 'IMAGE' | 'VIDEO' | 'DOCUMENT';
  name: string | null;
};

/** One emoji's tally on a message, collapsed server-side (messages.getMessages). */
export type MessageReaction = {
  emoji: string;
  count: number;
  /** Whether the viewer is one of the reactors — drives the toggle state. */
  reacted: boolean;
};

/** Emoji offered by the reaction picker. Deliberately short: a long list turns
 *  into a decision, and any of these reads fine at 14px. */
export const QUICK_REACTIONS = ['👍', '❤️', '😂', '🎉', '👀', '🙏'] as const;

/**
 * Apply one `messageReaction` socket delta to a tally list. Shared by web and
 * mobile so the transcript and the thread pane can never disagree about what a
 * pill should read.
 *
 * `count` is authoritative — it is read from the DB after the write, so a
 * racing double-click cannot drift the number.
 */
export const applyReaction = (
  current: MessageReaction[],
  emoji: string,
  added: boolean,
  count: number,
  isViewer: boolean
): MessageReaction[] => {
  const without = current.filter((r) => r.emoji !== emoji);
  if (count === 0) return without;
  const existing = current.find((r) => r.emoji === emoji);
  return [
    ...without,
    { emoji, count, reacted: isViewer ? added : (existing?.reacted ?? false) }
  ];
};

export type MessageDto = ConversationDto['lastMessage'] & {
  //added for front end processing
  read?: boolean;
  fileUrl?: string;
  fileType?: 'IMAGE' | 'VIDEO' | 'DOCUMENT';
  fileName?: string;
  attachments?: MessageAttachment[];
  reactions?: MessageReaction[];
  /** Replies under this message. Only root messages carry a thread. */
  replyCount?: number;
  lastReplyAt?: Date | null;
  /** Set on replies — a reply renders inside its thread pane, never inline. */
  parentId?: number | null;
  // Optimistic-UI flags (chat). Present only on temp messages until the real
  // server response replaces them — never persisted.
  pending?: boolean;
  failed?: boolean;
  // Local file URIs for previewing attachments while the actual upload is
  // still in flight; replaced by attachments[].key once the server confirms.
  pendingPreviewUris?: Array<{ uri: string; isVideo?: boolean; name?: string }>;
};
